import React, { useCallback, useRef } from 'react';
import { useStore } from '../../store';

interface ResizeHandleProps {
  nodeId: string;
}

export const ResizeHandle: React.FC<ResizeHandleProps> = ({ nodeId }) => {
  const updateNode = useStore((state) => state.updateNode);
  const nodes = useStore((state) => state.nodes);
  const isResizingRef = useRef(false);
  const startSizeRef = useRef({ width: 0, height: 0 });
  const startPosRef = useRef({ x: 0, y: 0 });

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.stopPropagation();
      e.preventDefault();

      const node = nodes[nodeId];
      if (!node) return;

      isResizingRef.current = true;
      startPosRef.current = { x: e.clientX, y: e.clientY };

      // Get current size or default
      const currentWidth = node.width || getDefaultWidth(node.type);
      const currentHeight = node.height || getDefaultHeight(node.type);
      startSizeRef.current = { width: currentWidth, height: currentHeight };

      const handleMouseMove = (e: MouseEvent) => {
        if (!isResizingRef.current) return;

        const deltaX = e.clientX - startPosRef.current.x;
        const deltaY = e.clientY - startPosRef.current.y;

        let newWidth = startSizeRef.current.width + deltaX;
        let newHeight = startSizeRef.current.height + deltaY;

        // Apply constraints: min 140x100px, max 400x400px
        newWidth = Math.max(140, Math.min(400, newWidth));
        newHeight = Math.max(100, Math.min(400, newHeight));

        // For circle nodes, maintain aspect ratio (make it square)
        if (node.type === 'circle') {
          const size = Math.max(newWidth, newHeight);
          newWidth = size;
          newHeight = size;
        }

        // For diamond nodes, maintain aspect ratio (make it square)
        if (node.type === 'diamond') {
          const size = Math.max(newWidth, newHeight);
          newWidth = size;
          newHeight = size;
        }

        updateNode(nodeId, { width: newWidth, height: newHeight }, { skipHistory: true });
      };

      const handleMouseUp = () => {
        if (isResizingRef.current) {
          isResizingRef.current = false;
          // Final update with history
          const node = nodes[nodeId];
          if (node && (node.width !== undefined || node.height !== undefined)) {
            updateNode(nodeId, { width: node.width, height: node.height });
          }
        }
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };

      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    },
    [nodeId, nodes, updateNode]
  );

  return (
    <div
      className="node-resize-handle"
      onMouseDown={handleMouseDown}
      title="Drag to resize"
    >
      <svg width="10" height="10" viewBox="0 0 10 10" fill="none">
        <path d="M10 0L10 10L0 10" stroke="currentColor" strokeWidth="1.5" />
        <path d="M6 4L6 10L0 10L0 4" stroke="currentColor" strokeWidth="1.5" opacity="0.5" />
      </svg>
    </div>
  );
};

// Helper functions to get default dimensions for each node type
function getDefaultWidth(type: string): number {
  switch (type) {
    case 'rectangle':
      return 140;
    case 'circle':
    case 'diamond':
      return 120;
    default:
      // task, note, milestone, text
      return 160;
  }
}

function getDefaultHeight(type: string): number {
  switch (type) {
    case 'rectangle':
      return 100;
    case 'circle':
    case 'diamond':
      return 120;
    default:
      // task, note, milestone, text - no fixed height, let content determine
      return 100;
  }
}
